/**
 * Centralized logging utility with development-only output.
 * In production builds, all logs are silently suppressed.
 */

const isDev = import.meta.env.DEV;

type LogLevel = "debug" | "info" | "warn" | "error";

interface LoggerOptions {
  prefix?: string;
}

const formatMessage = (level: LogLevel, prefix: string | undefined, message: string): string => {
  const timestamp = new Date().toISOString();
  const levelTag = `[${level.toUpperCase()}]`;
  const prefixTag = prefix ? `[${prefix}]` : "";
  return `${timestamp} ${levelTag}${prefixTag} ${message}`;
};

/**
 * Logger instance with methods for different log levels.
 * All methods are no-ops in production.
 */
export const logger = {
  /**
   * Debug-level logging - only in development
   */
  debug: (message: string, ...args: unknown[]): void => {
    if (isDev) {
      console.debug(formatMessage("debug", undefined, message), ...args);
    }
  },

  /**
   * Info-level logging - only in development
   */
  info: (message: string, ...args: unknown[]): void => {
    if (isDev) {
      console.info(formatMessage("info", undefined, message), ...args);
    }
  },

  /**
   * Warning-level logging - only in development
   */
  warn: (message: string, ...args: unknown[]): void => {
    if (isDev) {
      console.warn(formatMessage("warn", undefined, message), ...args);
    }
  },

  /**
   * Error-level logging - only in development
   */
  error: (message: string, ...args: unknown[]): void => {
    if (isDev) {
      console.error(formatMessage("error", undefined, message), ...args);
    }
  },

  /**
   * Create a namespaced logger with a prefix
   */
  createLogger: (options: LoggerOptions) => ({
    debug: (message: string, ...args: unknown[]): void => {
      if (isDev) {
        console.debug(formatMessage("debug", options.prefix, message), ...args);
      }
    },
    info: (message: string, ...args: unknown[]): void => {
      if (isDev) {
        console.info(formatMessage("info", options.prefix, message), ...args);
      }
    },
    warn: (message: string, ...args: unknown[]): void => {
      if (isDev) {
        console.warn(formatMessage("warn", options.prefix, message), ...args);
      }
    },
    error: (message: string, ...args: unknown[]): void => {
      if (isDev) {
        console.error(formatMessage("error", options.prefix, message), ...args);
      }
    },
  }),
};

export default logger;
